import { server } from "../../config/ConfigFirebase";
import { getToken } from "../auth/AuthServices";

// Tabla de Retención Documental: the `handleTrd` Cloud Function owns it and
// authorizes every action from the caller's stored profile (trd.* permissions).
//
// Like UserReportService these answer { body, error, errors } rather than null
// on error: the page must tell "you may not" (FORBIDDEN, a real case because
// the frontend unions live role permissions while the backend reads the stored
// snapshot) apart from a conflict or a validation failure it can show.

const trdUrl = () =>
  process.env.REACT_APP_TRD_URL || `https://${server}.cloudfunctions.net/handleTrd`;

const callTrd = async (body) => {
  try {
    const token = await getToken();
    const resp = await fetch(trdUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + token,
      },
      body: JSON.stringify(body),
    });
    if (resp.status === 401 || resp.status === 403) return { body: null, error: "FORBIDDEN", errors: [] };
    const json = await resp.json().catch(() => null);
    if (!resp.ok || !json?.result) {
      return { body: null, error: json?.error || "FAILED", errors: json?.errors || [], detail: json };
    }
    return { body: json.body, error: null, errors: [] };
  } catch (e) {
    console.error(`handleTrd ${body.action} error:`, e);
    return { body: null, error: "FAILED", errors: [] };
  }
};

// body: { dependencies: [{ dependenceCode, name, active, current, draft, versionCount }] }
export const listTrdDependencies = () => callTrd({ action: "LIST_DEPENDENCIES" });

// The shared list of document types: what the existing TRD already uses plus
// every type created since. body: { types: [{ name, uses }] }.
export const listTrdDocumentTypes = () => callTrd({ action: "LIST_DOCUMENT_TYPES" });

// body: { versions: [...] }, newest first.
export const listTrdVersions = (code) => callTrd({ action: "LIST_VERSIONS", code });

// Without `version`: the open draft, else the current version.
// body: { version: { dependenceCode, version, status, name, active, series, approval, ... } }
export const getTrdVersion = (code, version) =>
  callTrd({ action: "GET_VERSION", code, ...(version ? { version } : {}) });

export const createTrdDependence = (code, name) =>
  callTrd({ action: "CREATE_DEPENDENCE", code, name });

// The whole draft goes at once: { name, table_number, active, series: [{ code, name,
// format_code, subseries: [{ code, name, retention: { management, central },
// final_disposition: { total_conservation, selection }, procedure, filetypes, ... }] }] }.
export const saveTrdDraft = (code, version, draft) =>
  callTrd({ action: "SAVE_DRAFT", code, version, draft });

export const createTrdDraft = (code) => callTrd({ action: "CREATE_DRAFT", code });

export const discardTrdDraft = (code, version) => callTrd({ action: "DISCARD_DRAFT", code, version });

// approval: { actNumber, actDate (YYYY-MM-DD), issuingBody, convalidation }
export const publishTrdVersion = (code, version, approval) =>
  callTrd({ action: "PUBLISH", code, version, approval });

// body: { imported, skipped, invalid: [{ docId, error }] }
export const importExistingTrd = () => callTrd({ action: "IMPORT_LEGACY" });

// Builds the .xlsx on the server and saves it. Returns null when downloaded,
// else the error code. Without `code`: every current dependence.
export const downloadTrdXlsx = async (code, version) => {
  const { body, error } = await callTrd({
    action: "EXPORT", ...(code ? { code } : {}), ...(version ? { version } : {}),
  });
  if (error || !body?.base64) return error || "FAILED";
  const bytes = Uint8Array.from(atob(body.base64), (c) => c.charCodeAt(0));
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = body.fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoked a moment later: revoking right after click() can cancel the
  // download before the browser has started reading the blob.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
  return null;
};
